import { useSelector } from 'react-redux';
import { Box } from '@chakra-ui/react';
import { ApplicationState } from '../../../../../store';
import { CustomHeading } from 'shared-components';
import Field from '../../../../Field/Field';
import { PrevNextButtons } from '../../../../PrevNextButtons';
import { FileList } from '../../../../FileList/FileList';
import { EnglishTestNotReqdReason, EnglishTestType } from 'types';
import { useNavigate, useParams } from 'react-router-dom';
import ApplicantHeader from '../../../../../components/molecules/applicantHeader';
import { useMemo, JSX } from 'react';
import { formatYearMonthDate } from '@/utilities/dateHelpers';
import { SectionHeader, SectionTitles } from '@/components/molecules/SectionHeader';
import { getApplicationData } from '@/components/pages/applicant-dashboard/sections/helpers/getApplicationData';
import { useTaskNavigation } from '@/hooks/useTaskNavigation';
import {
  getEnglishTestDataFromFulfilment,
  mergeEnglishTestData,
  getMergedReasonNotReqd,
  getMergedReasonNotReqdOther,
  getQualificationLabel,
  getNotRequiredReason,
  hasReasonNotRequiredOther,
  hasReasonNotRequired,
} from '@/components/pages/applicant-dashboard/sections/helpers/getEnglishLanguageData';
import { useTaskRouteGuard } from '@/hooks/useTaskRouteGuard';
import { TaskLoadingSkeleton } from '@/components/molecules/TaskLoadingSkeleton';
import type { IApplicantOffer } from 'types';

export default function EnglishLanguage(): JSX.Element {
  const { application, allDocuments, offer, offers } = useSelector((state: ApplicationState) => state.apply);
  const params = useParams();
  const { nextRoute, previousRoute } = useTaskNavigation();
  const { offerId } = params;

  // Route guard - redirects if task is disabled
  const { isLoading } = useTaskRouteGuard('/english-language');

  if (isLoading) {
    return <TaskLoadingSkeleton />;
  }

  // Get the current offer: prefer state.apply.offer, otherwise find by offerId from URL, otherwise use first offer
  const currentOffer = useMemo<IApplicantOffer | undefined>(() => {
    // First try state.apply.offer (if it's populated)
    if (offer && offer.id) {
      return offer;
    }
    // If offerId is in URL params, find matching offer from offers array
    if (offerId && offers && offers.length > 0) {
      return offers.find(o => o.id === offerId);
    }
    // Fallback to first offer if available
    return offers && offers.length > 0 ? offers[0] : undefined;
  }, [offer, offers, offerId]);

  // Get fulfilmentData from offer requirements if available (this contains the updated data)
  const fulfilmentEnglishTestData = useMemo(() => getEnglishTestDataFromFulfilment(currentOffer), [currentOffer]);

  const { initial, current } = getApplicationData(application);

  // Merge fulfilmentData with application data, prioritizing fulfilmentData
  const mergedCurrent = useMemo(() => {
    const hasEnglishTestValue = fulfilmentEnglishTestData?.hasTakenTest ?? current.hasEnglishTest;
    return {
      ...current,
      englishTest: mergeEnglishTestData(current.englishTest, fulfilmentEnglishTestData),
      hasEnglishTest: hasEnglishTestValue,
      // Clear reasonNotReqd when test is taken
      reasonNotReqd: hasEnglishTestValue
        ? undefined
        : getMergedReasonNotReqd(current.reasonNotReqd, fulfilmentEnglishTestData),
      reasonNotReqdOther: hasEnglishTestValue
        ? undefined
        : getMergedReasonNotReqdOther(current.reasonNotReqdOther, fulfilmentEnglishTestData),
    };
  }, [current, fulfilmentEnglishTestData]);

  // Merge current fulfilmentData with initial application data for comparison
  // Prioritize current fulfilmentData over initial application data
  const mergedInitial = useMemo(() => {
    // If we have current fulfilmentData, use it directly instead of merging with initial
    if (fulfilmentEnglishTestData) {
      return {
        ...initial,
        englishTest: mergeEnglishTestData(initial.englishTest, fulfilmentEnglishTestData),
        hasEnglishTest: fulfilmentEnglishTestData.hasTakenTest ?? initial.hasEnglishTest,
        // Prioritize current fulfilmentData - use it directly if available, otherwise use initial
        reasonNotReqd:
          fulfilmentEnglishTestData.hasTakenTest === false
            ? (fulfilmentEnglishTestData.reasonNotReqd ?? initial.reasonNotReqd)
            : initial.reasonNotReqd,
        reasonNotReqdOther:
          fulfilmentEnglishTestData.hasTakenTest === false
            ? (fulfilmentEnglishTestData.reasonNotReqdOther ?? initial.reasonNotReqdOther)
            : initial.reasonNotReqdOther,
      };
    }
    // If no current fulfilmentData, use initial application data
    return initial;
  }, [initial, fulfilmentEnglishTestData]);

  const { hasEnglishTest, englishTest, reasonNotReqd, reasonNotReqdOther, englishQualification } = mergedCurrent;
  const {
    hasEnglishTest: initialHasEnglishTest,
    englishTest: initialEnglishTest,
    reasonNotReqd: initialReasonNotReqd,
    reasonNotReqdOther: initialReasonNotReqdOther,
    englishQualification: initialEnglishQualification,
  } = mergedInitial;

  const navigate = useNavigate();

  const files = allDocuments?.englishTest || [];

  const isOtherEnglishTest = englishTest.hasTakenTest && englishTest.type === EnglishTestType.Other;
  const initialIsOtherEnglishTest =
    initialEnglishTest.hasTakenTest && initialEnglishTest.type === EnglishTestType.Other;

  const isPrevQualification = reasonNotReqd === EnglishTestNotReqdReason.PrevQual;
  const initialIsPrevQualification = initialReasonNotReqd === EnglishTestNotReqdReason.PrevQual;

  // We assemble a list of all possible qualifications given the student's study history.
  const educations = { latest: application.studyHistory.highest, ...application.studyHistory.previous };
  const qualificationOptions = useMemo(() => {
    return Object.entries(educations ?? {}).reduce((prev, [_educationId, education]) => {
      const options = Object.entries(education?.qualifications ?? {}).map(([qualificationId, qualification]) => ({
        label: getQualificationLabel(qualification, education),
        value: qualificationId,
      }));

      prev.push(...options);

      return prev;
    }, []);
  }, [educations]);

  // We find the qualification that matches the english qualification ID saved.
  const qualification = isPrevQualification && qualificationOptions?.find(qual => qual?.value === englishQualification);
  const initialQualification =
    initialIsPrevQualification && qualificationOptions?.find(qual => qual?.value === initialEnglishQualification);

  const notRequiredReason = getNotRequiredReason(reasonNotReqd, isPrevQualification, qualification, hasEnglishTest);

  const initialNotRequiredReason = getNotRequiredReason(
    initialReasonNotReqd,
    initialIsPrevQualification,
    initialQualification,
    initialHasEnglishTest,
  );

  const testTakenData = englishTest.hasTakenTest ? englishTest : null;
  const initialTestTakenData = initialEnglishTest.hasTakenTest ? initialEnglishTest : null;

  const englishTestType = isOtherEnglishTest ? testTakenData?.typeOther : testTakenData?.type;
  const initialEnglishTestType = initialIsOtherEnglishTest
    ? initialTestTakenData?.typeOther
    : initialTestTakenData?.type;

  // Determine if test was taken based on the merged englishTest object (most reliable)
  const currentTestTaken = englishTest.hasTakenTest === true;
  const previousTestTaken = initialEnglishTest.hasTakenTest === true;

  const showEnglishTest = currentTestTaken || previousTestTaken;
  // Only show exempt reason if current state is explicitly "not taken"
  const showExemptReason = !currentTestTaken;

  // Only check for reason fields if we're actually showing the exempt reason section
  const hasReasonNotRequiredOtherValue =
    showExemptReason && hasReasonNotRequiredOther(reasonNotReqd, initialReasonNotReqd);
  const hasReasonNotRequiredValue = showExemptReason && hasReasonNotRequired(reasonNotReqd, initialReasonNotReqd);

  return (
    <>
      <ApplicantHeader pageName="Application" />
      <Box>
        <SectionHeader title={SectionTitles.ENGLISH_LANGUAGE} />

        <FileList files={files} />

        <CustomHeading level="h2" mb={2} mt={6} fontSize="md">
          Data
        </CustomHeading>

        <Box>
          <>
            {showEnglishTest && (
              <>
                <Field
                  leftLabel="Test type"
                  value={englishTestType ?? 'English test type is unknown.'}
                  originalValue={initialEnglishTestType ?? 'English test type is unknown'}
                />
                <Field
                  leftLabel="Reference number"
                  value={testTakenData?.referenceNo}
                  originalValue={initialTestTakenData?.referenceNo}
                />
                <Field
                  leftLabel="Test score(s)"
                  value={testTakenData?.score}
                  originalValue={initialTestTakenData?.score}
                />
                <Field
                  leftLabel="Date"
                  value={formatYearMonthDate(testTakenData?.date)}
                  originalValue={formatYearMonthDate(initialTestTakenData?.date)}
                />
                <Field
                  leftLabel="Valid until"
                  value={formatYearMonthDate(testTakenData?.validUntil)}
                  originalValue={formatYearMonthDate(initialTestTakenData?.validUntil)}
                />
              </>
            )}
            {showExemptReason && (
              <>
                {hasReasonNotRequiredOtherValue && (
                  <Field
                    leftLabel={'Custom reason not required'}
                    value={reasonNotReqdOther}
                    originalValue={initialReasonNotReqdOther || initialNotRequiredReason}
                  />
                )}
                {hasReasonNotRequiredValue && (
                  <Field
                    leftLabel="English met reason"
                    value={notRequiredReason}
                    originalValue={initialNotRequiredReason || initialReasonNotReqdOther}
                  />
                )}
              </>
            )}
          </>
        </Box>

        <Box mt={6}>
          <PrevNextButtons
            onPrevious={previousRoute ? () => navigate(previousRoute) : undefined}
            onNext={nextRoute ? () => navigate(nextRoute) : undefined}
          />
        </Box>
      </Box>
    </>
  );
}
